import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography,
  Box,
  Modal,
  Avatar,
  Tooltip,
  IconButton,
  Drawer,
  Divider,
  Badge,
} from '@mui/material';
import Logo from '../../assets/oniLogo.png';
import { useLocation, useNavigate } from 'react-router-dom';
import loginBackground from '../../assets/mainBackground.jpg'
import { store } from '../../redux/store';
import * as loginActions from '../../redux/actions/loginActions';
import HospitalSelection from '../Auth/HospitalSelection';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import DoctorSwitcher from '../Auth/DoctorSwitcher';
import { useSelector } from 'react-redux';
import { trackEvent } from '../../utils/firebase';
import * as providerActions from '../../redux/actions/providerActions';
import { getSignedImageUrl } from '../../utils/service';
import { useDoctor } from '../../context/DoctorSelectionContext';
import { useToast } from '../../context/ToastContext';
import { requestFcmPermissionAndRegister, showSystemDesktopNotification } from '../../services/fcmNotificationService';
import QRCode from 'react-qr-code';

const style = {
  width: {
    lg: '25%',
    xl: '25%',
    md: '25%',
    sm: '35%',
  },
  bgcolor: '#F2F2F2',
  outline: 'none',
  borderRadius: '10px',
  p: 2,
  height: '10%',
  justifyContent: 'space-between',
  display: 'flex',
  flexDirection: 'column'
};

const actionButtonStyle = { cursor: 'pointer', display: 'flex', backgroundColor: '#FFF', padding: 1, borderRadius: '10px', boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)' }

const Navbar = () => {
  const location = useLocation()
  const pathName = location.pathname
  const isAuthPage = pathName === '/' || pathName === '/verify-otp';
  const isPlainPage = ['dashboard', 'profile', 'video-call'].some((page) => pathName.includes(page)); // no photo behind the bar
  const isBillingPage = pathName.includes('billing');
  const isDashboardPage = pathName.includes('dashboard') || isBillingPage;
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const { selectedDoctor } = useDoctor();
  const navigate = useNavigate();
  const authReducer = useSelector((state) => state.authReducer);
  const isReceptionOrNurse = authReducer?.userAttributes?.userTenant?.role?.name == "RECEPTIONIST" || authReducer?.userAttributes?.userTenant?.role?.name == "NURSE"
  const canViewBilling = authReducer?.userAttributes?.userTenant?.role?.permissions?.includes('BILL_VIEW');

  const closeMobileMenu = () => setMobileMenuOpen(false);
  const notify = useToast();
  const [notifPermission, setNotifPermission] = useState(() => {
    return typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'granted';
  });

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setNotifPermission(Notification.permission);
    }
  }, []);

  const handleNotificationBellClick = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      if (notify) notify('Push notifications are not supported in this browser.', 'warning');
      return;
    }

    if (Notification.permission === 'granted') {
      if (notify) notify('Push notifications are active!', 'success');
      return;
    }

    if (Notification.permission === 'denied') {
      if (notify) {
        notify('Notifications are blocked by your browser. Click the lock 🔒 / tune icon in the address bar and select Allow.', 'error');
      }
      return;
    }

    // Permission is 'default' -> Explicit user gesture ensures prompt opens
    try {
      const res = await requestFcmPermissionAndRegister();
      if (typeof window !== 'undefined' && 'Notification' in window) {
        setNotifPermission(Notification.permission);
      }
      if (res?.success) {
        if (notify) notify('Push notifications enabled successfully!', 'success');
      } else if (Notification.permission === 'denied') {
        if (notify) notify('Notifications blocked. Enable in site settings (lock 🔒 icon) to get AI card alerts.', 'warning');
      }
    } catch (err) {
      console.error('Failed to request permission:', err);
    }
  };

  useEffect(() => {
    const accessToken = localStorage.getItem('token')
    if (accessToken) {
      store.dispatch(providerActions.getProviderDetailsRequestAction());
    }
  }, [])

  const providerReducer = useSelector((state) => state.providerReducer);
  const [imageUrl, setImageUrl] = useState(null);
  const [profileData, setProfileData] = useState({})

  useEffect(() => {
    if (providerReducer?.providerInfo) {
      setProfileData(providerReducer?.providerInfo);
    }
  }, [providerReducer]);


  useEffect(() => {
    let mounted = true;

    const loadImage = async () => {
      const mediaId = profileData?.basicInfo?.profileImageMediaId;
      if (!mediaId) return;
      const url = await getSignedImageUrl(mediaId);
      if (mounted) {
        setImageUrl(url);
      }
    };
    loadImage();
    return () => {
      mounted = false;
    };
  }, [profileData?.basicInfo?.profileImageMediaId]);

  return (
    !pathName.includes('consultation') && <AppBar position={isAuthPage ? "absolute" : "static"}
      sx={{
        backgroundImage: isAuthPage || isPlainPage ? 'none' : `url(${loginBackground})`,
        backgroundSize: '100% 100%',
        backdropFilter: isAuthPage || isPlainPage ? 'none' : 'blur(15px)',
        WebkitBackdropFilter: isAuthPage || isPlainPage ? 'none' : 'blur(15px)',
        backgroundColor: 'transparent',
        color: isPlainPage ? "#00000" : '#ffff',
        boxShadow:'none',
        width: '100%',
        zIndex: 1100
      }}
    >
      <Toolbar>
        {/* Logo and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
          <img src={Logo} alt="Logo" style={{ height: 50, marginRight: 10 }} />
          <Typography variant={'h5'} component="div" sx={{ fontSize: { xs: '18px', sm: '20px', md: '24px' },fontWeight:'bold' }}>
            ONI AI
          </Typography>
        </Box>

        {isDashboardPage &&
          <>
            {!isBillingPage && isReceptionOrNurse && <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', }}>
              {selectedDoctor?.shortUrl && (
                <Box sx={{ cursor: 'pointer', display: 'flex', padding: 0.5, backgroundColor: '#F2F2F2', borderRadius: '10px', mr: 1 }} onClick={() => setShowQrModal(true)}>
                  <QRCode value={selectedDoctor.shortUrl} size={35} />
                </Box>
              )}
            <DoctorSwitcher />
          </Box>}
            <Box sx={{ flexDirection: 'row', display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              {!isReceptionOrNurse && <Box sx={{ cursor: 'pointer', display: 'flex', padding: 1, borderRadius: '10px', alignItems: 'center', marginRight: '30px' }} onClick={() => {
                navigate('/profile')
              }}>
                <Avatar src={imageUrl || undefined} sx={{width: 52, height: 52 }} />
              </Box>}
              <HospitalSelection />
              {canViewBilling && !isBillingPage && <Box sx={{ cursor: 'pointer', display: 'flex', backgroundColor: '#F2F2F2', padding: 1, borderRadius: '10px', boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)', alignItems: 'center', marginLeft: '16px' }} onClick={() => navigate('/billing')}>
                <Typography sx={{ fontSize: '15px' }}>Billing</Typography>
              </Box>}
              {isBillingPage && <Box sx={{ cursor: 'pointer', display: 'flex', backgroundColor: '#F2F2F2', padding: 1, borderRadius: '10px', boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)', alignItems: 'center', marginLeft: '16px' }} onClick={() => navigate('/dashboard')}>
                <Tooltip title="Go to dashboard"><HomeIcon sx={{ color: '#808080' }} /></Tooltip>
              </Box>}
              <Tooltip title={
                notifPermission === 'granted'
                  ? 'Notifications active (AI cards ready alerts enabled)'
                  : notifPermission === 'denied'
                  ? 'Notifications blocked (click to see how to enable)'
                  : 'Click to enable AI card notifications'
              }>
                <IconButton
                  onClick={handleNotificationBellClick}
                  data-testid="navbar-notif-bell"
                  aria-label="Notification settings"
                  sx={{
                    backgroundColor: '#F2F2F2',
                    borderRadius: '10px',
                    boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)',
                    marginLeft: '16px',
                    padding: '8px',
                    '&:hover': { backgroundColor: '#E5E7EB' }
                  }}
                >
                  <Badge
                    variant="dot"
                    color={notifPermission === 'granted' ? 'success' : notifPermission === 'denied' ? 'error' : 'warning'}
                  >
                    {notifPermission === 'granted' ? (
                      <NotificationsActiveIcon sx={{ color: '#10B981', fontSize: 22 }} />
                    ) : notifPermission === 'denied' ? (
                      <NotificationsOffIcon sx={{ color: '#EF4444', fontSize: 22 }} />
                    ) : (
                      <NotificationsNoneIcon sx={{ color: '#F59E0B', fontSize: 22 }} />
                    )}
                  </Badge>
                </IconButton>
              </Tooltip>
              <Box sx={{ cursor: 'pointer', display: 'flex', backgroundColor: '#F2F2F2', padding: 1, borderRadius: '10px', boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)', alignItems: 'center', marginLeft: '20px' }} onClick={() => {
                setShowDropdown((prev) => !prev);
              }}>
                <Typography sx={{ fontSize: '15px' }}>
                  Sign out
                </Typography>
              </Box>
            </Box>
            {/* Mobile hamburger */}
            <IconButton
              sx={{ display: { xs: 'inline-flex', md: 'none' }, color: 'inherit' }}
              onClick={() => {
                trackEvent('nav_menu_opened');
                setMobileMenuOpen(true);
              }}
              aria-label="Open menu"
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="right"
              open={mobileMenuOpen}
              onClose={closeMobileMenu}
              PaperProps={{ sx: { width: '78%', maxWidth: 320, backgroundColor: '#FFFF', p: 2 } }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>Menu</Typography>
                <IconButton onClick={closeMobileMenu} aria-label="Close menu">
                  <CloseIcon />
                </IconButton>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {/* <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', p: 1.25, borderRadius: '10px', mb: 1.5, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
                onClick={() => { closeMobileMenu(); navigate('/profile'); }}
              >
                <Avatar sx={{ width: 36, height: 36, mr: 1.5 }}>
                  <PersonIcon />
                </Avatar>
                <Typography sx={{ fontSize: '15px' }}>Profile</Typography>
              </Box> */}

              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: '12px', color: '#666', mb: 0.5, ml: 0.5 }}>
                  Hospital
                </Typography>
                <Box sx={{ display: 'flex', '& > *': { width: '100% !important', maxWidth: '100% !important' } }}>
                  <HospitalSelection />
                </Box>
              </Box>
              {!isBillingPage && isReceptionOrNurse  && <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: '12px', color: '#666', mb: 0.5, ml: 0.5 }}>
                  Doctor
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {selectedDoctor?.shortUrl && (
                  <Box sx={{ cursor: 'pointer', display: 'flex', padding: 0.5, backgroundColor: '#F2F2F2', borderRadius: '10px', mr: 1 }} onClick={() => { setShowQrModal(true); closeMobileMenu(); }}>
                    <QRCode value={selectedDoctor.shortUrl} size={35} />
                  </Box>
                )}
                <Box sx={{ flexGrow: 1, '& > *': { width: '100% !important', maxWidth: '100% !important' } }}>
                  <DoctorSwitcher />
                </Box>
              </Box>
            </Box>}

              <Divider sx={{ my: 1 }} />

              {canViewBilling && !isBillingPage && <Box
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 1.25, borderRadius: '10px', mt: 1 }}
                onClick={() => { closeMobileMenu(); navigate('/billing'); }}
              >
                <Typography sx={{ fontSize: '15px' }}>Billing</Typography>
              </Box>}
              {isBillingPage && <Box
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 1.25, borderRadius: '10px', mt: 1 }}
                onClick={() => { closeMobileMenu(); navigate('/dashboard'); }}
              >
                <HomeIcon sx={{ mr: 1, fontSize: 20 }} />
                <Typography sx={{ fontSize: '15px' }}>Dashboard</Typography>
              </Box>}

              <Box
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', backgroundColor: '#F2F2F2', padding: 1.25, borderRadius: '10px', boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)', mt: 1 }}
                onClick={() => { closeMobileMenu(); handleNotificationBellClick(); }}
              >
                <Badge
                  variant="dot"
                  color={notifPermission === 'granted' ? 'success' : notifPermission === 'denied' ? 'error' : 'warning'}
                  sx={{ mr: 1.5 }}
                >
                  {notifPermission === 'granted' ? (
                    <NotificationsActiveIcon sx={{ color: '#10B981', fontSize: 20 }} />
                  ) : notifPermission === 'denied' ? (
                    <NotificationsOffIcon sx={{ color: '#EF4444', fontSize: 20 }} />
                  ) : (
                    <NotificationsNoneIcon sx={{ color: '#F59E0B', fontSize: 20 }} />
                  )}
                </Badge>
                <Typography sx={{ fontSize: '15px' }}>
                  {notifPermission === 'granted' ? 'Notifications Active' : 'Enable Notifications'}
                </Typography>
              </Box>

              <Box
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', backgroundColor: '#F2F2F2', padding: 1.25, borderRadius: '10px', boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)', mt: 1 }}
                onClick={() => { closeMobileMenu(); setShowDropdown((prev) => !prev); }}
              >
                <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                <Typography sx={{ fontSize: '15px' }}>Sign out</Typography>
              </Box>
            </Drawer>
          </>
        }

        {pathName.includes('profile') && <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center' }}>
          <Box sx={{
            cursor: 'pointer',
            display: 'flex',
            padding: 1,
            borderRadius: 15,
            marginRight: '30px',
            backgroundColor: 'rgba(238, 251, 255, 0.3)',
            boxShadow: '0px 2px 4px rgba(0,0,0,0.25)',
            height: 30,
            widows: 30
          }} onClick={() => {
            navigate('/dashboard')
          }}>
            <Tooltip title="Press it to go to the dashboard">
              <HomeIcon sx={{ width: 32, height: 32, color: '#808080' }} />
            </Tooltip>
          </Box>
        </Box>}

        <Modal
          open={showDropdown}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          BackdropProps={{
            sx: {
              backdropFilter: 'blur(5px)',
            }
          }}
        >
          <Box sx={style}>
            <Typography sx={{ textAlign: 'center', fontSize: { xl: '19px', lg: '17px', md: '15px' } }}>Are you sure you want to logout ?</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', }}>
              <Box sx={actionButtonStyle} onClick={() => {
                trackEvent('logout_clicked');
                store.dispatch(loginActions.authLogoutRequestAction())
              }}>
                <Typography>
                  Confirm
                </Typography>
              </Box>
              <Box sx={actionButtonStyle} onClick={() => {
                setShowDropdown((prev) => !prev);
              }}>
                <Typography>
                  Cancel
                </Typography>
              </Box>
            </Box>
          </Box>
        </Modal>
        <Modal
          open={showQrModal}
          onClose={() => setShowQrModal(false)}
          aria-labelledby="qr-modal-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          BackdropProps={{
            sx: { backdropFilter: 'blur(5px)' }
          }}
        >
          <Box sx={{
            bgcolor: '#FFF',
            borderRadius: '16px',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: 24,
            position: 'relative'
          }}>
            <IconButton
              sx={{ position: 'absolute', top: 8, right: 8 }}
              onClick={() => setShowQrModal(false)}
            >
              <CloseIcon />
            </IconButton>
            <Typography id="qr-modal-title" variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              Scan QR Code
            </Typography>
            {selectedDoctor?.shortUrl && (
              <QRCode value={selectedDoctor.shortUrl} size={250} />
            )}
          </Box>
        </Modal>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
